import * as React from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Check, Info, Ruler, RotateCcw } from 'lucide-react';
import { api } from '@/lib/api';
import { useContexto } from '@/hooks/useContexto';
import { useSessao } from '@/hooks/useSessao';
import { cn, dataHora } from '@/lib/utils';
import { Botao } from '@/componentes/ui/botao';
import { EstadoErro } from '@/componentes/ui/estados';
import { Campo, Esqueleto, Pilula } from '@/componentes/ui/primitivos';

/**
 * Medida da vaga DESTA posição.
 *
 * A regra é uma cadeia de três níveis, o ISNULL encadeado que a operação
 * pediu:
 *
 *     medida do LOCALIZADOR  →  medida do CENTRO  →  padrão do sistema
 *
 * O normal é a posição seguir o CD. Esta tela existe para a exceção: a vaga
 * de flow-rack no meio do porta-palete, o nível de piso mais alto, a vaga
 * dupla. Cadastrar aqui afeta SÓ esta posição.
 */

export type OrigemDaMedida = 'localizador' | 'centro' | 'padrao';

interface DimensoesPosicao {
  localizador: number;
  centroEstoque: number;
  vagaLarguraM: number;
  vagaProfundidadeM: number;
  nivelAlturaM: number;
  origem: OrigemDaMedida;
  volumeM3: number;
  observacao: string | null;
  atualizadoEm: string | null;
  padraoDoCentro: {
    vagaLarguraM: number;
    vagaProfundidadeM: number;
    nivelAlturaM: number;
  };
}

const ROTULO_ORIGEM: Record<OrigemDaMedida, { texto: string; detalhe: string }> = {
  localizador: {
    texto: 'medida própria',
    detalhe: 'Esta posição foge do padrão do CD e tem medida cadastrada só para ela.',
  },
  centro: {
    texto: 'padrão do CD',
    detalhe: 'Usando a medida cadastrada para este centro em Admin → Centros de estoque.',
  },
  padrao: {
    texto: 'padrão do sistema',
    detalhe:
      'Ninguém mediu este CD ainda: está usando o padrão de mercado (135 × 100 × 150 cm). Cadastre em Admin → Centros de estoque.',
  },
};

export function MedidaPosicao({ localizador }: { localizador: number }): JSX.Element {
  const { centro } = useContexto();
  const { usuario } = useSessao();
  const cliente = useQueryClient();
  const podeEditar = usuario?.adminMaster ?? false;
  const [editando, definirEditando] = React.useState(false);

  const consulta = useQuery<DimensoesPosicao>({
    queryKey: ['medida-posicao', centro, localizador],
    queryFn: () =>
      api.get<DimensoesPosicao>(`/api/localizador/${localizador}/porta-palete`, { centro }),
    enabled: centro !== null,
    staleTime: 60_000,
  });

  const invalidar = (): void => {
    void cliente.invalidateQueries({ queryKey: ['medida-posicao'] });
    void cliente.invalidateQueries({ queryKey: ['mapa3d'] });
    void cliente.invalidateQueries({ queryKey: ['rel-localizadores'] });
    void cliente.invalidateQueries({ queryKey: ['posicao'] });
  };

  const salvar = useMutation({
    mutationFn: (corpo: {
      larguraCm: number;
      profundidadeCm: number;
      alturaCm: number;
      observacao: string | null;
    }) => api.put(`/api/localizador/${localizador}/porta-palete`, corpo, { centro }),
    onSuccess: () => {
      invalidar();
      definirEditando(false);
    },
  });

  const restaurar = useMutation({
    mutationFn: () => api.del(`/api/localizador/${localizador}/porta-palete`, { centro }),
    onSuccess: invalidar,
  });

  if (consulta.isLoading) return <Esqueleto className="h-32" />;
  if (consulta.isError) {
    return <EstadoErro erro={consulta.error} aoTentarDeNovo={() => void consulta.refetch()} />;
  }
  const d = consulta.data;
  if (!d) return <Esqueleto className="h-32" />;

  const cm = (m: number): number => Math.round(m * 100);
  const rotulo = ROTULO_ORIGEM[d.origem];

  if (editando) {
    return (
      <Formulario
        inicial={{
          larguraCm: cm(d.vagaLarguraM),
          profundidadeCm: cm(d.vagaProfundidadeM),
          alturaCm: cm(d.nivelAlturaM),
          observacao: d.observacao ?? '',
        }}
        padraoCentro={{
          larguraCm: cm(d.padraoDoCentro.vagaLarguraM),
          profundidadeCm: cm(d.padraoDoCentro.vagaProfundidadeM),
          alturaCm: cm(d.padraoDoCentro.nivelAlturaM),
        }}
        salvando={salvar.isPending}
        erro={salvar.error}
        aoCancelar={() => {
          salvar.reset();
          definirEditando(false);
        }}
        aoSalvar={(v) => salvar.mutate(v)}
      />
    );
  }

  return (
    <div className="space-y-3">
      <div className="cartao p-4">
        <div className="flex items-start justify-between gap-3">
          <div>
            <p className="rotulo">Tamanho da vaga</p>
            <p className="tabular mt-1 font-mono text-[17px] font-bold">
              {cm(d.vagaLarguraM)} × {cm(d.vagaProfundidadeM)} × {cm(d.nivelAlturaM)} cm
            </p>
            <p className="tabular mt-0.5 text-[12px] text-textoFraco">
              {d.volumeM3.toLocaleString('pt-BR', { minimumFractionDigits: 3 })} m³ de volume útil
            </p>
          </div>
          <Pilula tom={d.origem === 'localizador' ? 'marca' : 'neutro'}>{rotulo.texto}</Pilula>
        </div>

        <p className="mt-3 flex items-start gap-2 border-t border-borda pt-3 text-[11.5px] leading-relaxed text-textoFraco">
          <Info className="mt-0.5 h-3.5 w-3.5 shrink-0" />
          {rotulo.detalhe}
        </p>

        {d.origem === 'localizador' ? (
          <p className="mt-2 text-[11.5px] text-textoFraco">
            O padrão do CD é {cm(d.padraoDoCentro.vagaLarguraM)} ×{' '}
            {cm(d.padraoDoCentro.vagaProfundidadeM)} × {cm(d.padraoDoCentro.nivelAlturaM)} cm.
            {d.atualizadoEm ? ` Medida cadastrada em ${dataHora(d.atualizadoEm)}.` : ''}
          </p>
        ) : null}

        {d.observacao ? (
          <p className="mt-2 rounded-lg bg-superficie2 px-3 py-2 text-[11.5px] italic text-textoFraco">
            {d.observacao}
          </p>
        ) : null}
      </div>

      {podeEditar ? (
        <div className="flex items-center gap-2">
          <Botao variante="secundaria" tamanho="pequeno" onClick={() => definirEditando(true)}>
            <Ruler className="h-3.5 w-3.5" />
            {d.origem === 'localizador' ? 'Editar medida desta vaga' : 'Cadastrar medida própria'}
          </Botao>
          {d.origem === 'localizador' ? (
            <Botao
              variante="fantasma"
              tamanho="pequeno"
              carregando={restaurar.isPending}
              onClick={() => restaurar.mutate()}
            >
              <RotateCcw className="h-3.5 w-3.5" />
              Voltar ao padrão do CD
            </Botao>
          ) : null}
        </div>
      ) : (
        <p className="text-[11.5px] text-textoFraco">
          Só administrador master altera medida de posição.
        </p>
      )}

      {restaurar.isError ? <EstadoErro erro={restaurar.error} className="py-4" /> : null}
    </div>
  );
}

interface Valores {
  larguraCm: number;
  profundidadeCm: number;
  alturaCm: number;
  observacao: string;
}

function Formulario({
  inicial,
  padraoCentro,
  salvando,
  erro,
  aoSalvar,
  aoCancelar,
}: {
  inicial: Valores;
  padraoCentro: { larguraCm: number; profundidadeCm: number; alturaCm: number };
  salvando: boolean;
  erro: unknown;
  aoSalvar: (v: {
    larguraCm: number;
    profundidadeCm: number;
    alturaCm: number;
    observacao: string | null;
  }) => void;
  aoCancelar: () => void;
}): JSX.Element {
  const [largura, definirLargura] = React.useState(String(inicial.larguraCm));
  const [profundidade, definirProfundidade] = React.useState(String(inicial.profundidadeCm));
  const [altura, definirAltura] = React.useState(String(inicial.alturaCm));
  const [observacao, definirObservacao] = React.useState(inicial.observacao);

  const n = (v: string): number => Number(v.replace(',', '.'));
  const valido = [largura, profundidade, altura].every((v) => Number.isFinite(n(v)) && n(v) > 0);
  const volume = valido ? (n(largura) * n(profundidade) * n(altura)) / 1_000_000 : 0;
  const igualAoCentro =
    n(largura) === padraoCentro.larguraCm &&
    n(profundidade) === padraoCentro.profundidadeCm &&
    n(altura) === padraoCentro.alturaCm;

  return (
    <form
      className="space-y-3"
      onSubmit={(e) => {
        e.preventDefault();
        if (!valido) return;
        aoSalvar({
          larguraCm: n(largura),
          profundidadeCm: n(profundidade),
          alturaCm: n(altura),
          observacao: observacao.trim() || null,
        });
      }}
    >
      <div className="grid grid-cols-3 gap-2">
        <CampoCm rotulo="Largura" valor={largura} aoMudar={definirLargura} />
        <CampoCm rotulo="Profundidade" valor={profundidade} aoMudar={definirProfundidade} />
        <CampoCm rotulo="Altura" valor={altura} aoMudar={definirAltura} />
      </div>

      <p className="flex items-baseline gap-2">
        <span className="rotulo">Volume da vaga</span>
        <span className="tabular text-[16px] font-bold text-primaria">
          {valido ? volume.toLocaleString('pt-BR', { minimumFractionDigits: 3 }) : '—'} m³
        </span>
      </p>

      <label className="block">
        <span className="rotulo mb-1 block">Por que esta vaga foge do padrão</span>
        <Campo
          value={observacao}
          onChange={(e) => definirObservacao(e.target.value)}
          placeholder="Ex.: flow-rack, vaga dupla, nível de piso com pé-direito maior."
          maxLength={300}
          className="min-h-[38px] text-[13px]"
        />
      </label>

      {igualAoCentro ? (
        <p className="rounded-lg border border-status-atencao/30 bg-status-atencaoFundo px-3 py-2 text-[11.5px] text-textoFraco">
          Esta medida é igual à do CD. Salvar aqui cria uma exceção que não muda nada — se a vaga
          segue o padrão, é melhor não cadastrar.
        </p>
      ) : null}

      {erro ? <EstadoErro erro={erro} className="py-4" /> : null}

      <div className="flex items-center gap-2">
        <Botao
          type="submit"
          variante="primaria"
          tamanho="pequeno"
          disabled={!valido}
          carregando={salvando}
        >
          {!salvando ? <Check className="h-4 w-4" /> : null}
          Salvar medida desta vaga
        </Botao>
        <Botao type="button" variante="fantasma" tamanho="pequeno" onClick={aoCancelar}>
          Cancelar
        </Botao>
      </div>
    </form>
  );
}

function CampoCm({
  rotulo,
  valor,
  aoMudar,
}: {
  rotulo: string;
  valor: string;
  aoMudar: (v: string) => void;
}): JSX.Element {
  return (
    <label className="block">
      <span className="rotulo mb-1 block">{rotulo}</span>
      <span className="relative block">
        <Campo
          value={valor}
          onChange={(e) => aoMudar(e.target.value)}
          inputMode="decimal"
          aria-label={`${rotulo} em centímetros`}
          className={cn('tabular pr-8 text-[14px] font-semibold')}
        />
        <span className="pointer-events-none absolute right-2.5 top-1/2 -translate-y-1/2 text-[10.5px] font-semibold text-textoSuave">
          cm
        </span>
      </span>
    </label>
  );
}
